import React, { useState } from 'react';
import {
  Box, TextField, Button, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, InputAdornment,
} from '@mui/material';
import DrawerShell from './_drawer/DrawerShell';
import DrawerHeader from './_drawer/DrawerHeader';
import useResponsiveAnchor from './_drawer/useResponsiveAnchor';
import api from '../../utils/api';
import { RADIUS_LIBRARIAN as RADIUS } from '../../theme/tokens';

const DEFAULT_FORM = {
  plan_id: '',
  amount: '',
  payment_method: 'cash',
  transaction_id: '',
  notes: '',
};

function StudentPaymentDrawer({ open, onClose, studentId, plans, onSaved, showSnackbar }) {
  const anchor = useResponsiveAnchor();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  const handleClose = () => {
    setForm(DEFAULT_FORM);
    onClose();
  };

  const handlePlanSelect = (planId) => {
    const plan = plans.find((p) => p.id === planId);
    setForm({
      ...form,
      plan_id: planId,
      // price_cents is actually stored in RUPEES — auto-fill as rupees
      amount: plan ? plan.price_cents.toString() : form.amount,
    });
  };

  const handleSubmit = async () => {
    if (!form.amount || Number(form.amount) <= 0) {
      showSnackbar('Valid amount is required', 'error');
      return;
    }

    try {
      setSaving(true);
      await api.post(`/librarian/students/${studentId}/payments`, {
        plan_id: form.plan_id || null,
        amount_cents: parseInt(form.amount * 100), // rupees → paise
        payment_method: form.payment_method,
        transaction_id: form.transaction_id || null,
        notes: form.notes || null,
        status: 'completed',
      });
      showSnackbar('Payment recorded successfully', 'success');
      setForm(DEFAULT_FORM);
      onSaved();
      onClose();
    } catch (err) {
      showSnackbar('Failed to record payment', 'error');
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button onClick={handleClose} disabled={saving} sx={{ borderRadius: RADIUS.md }}>
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={saving}
        fullWidth
        sx={{ borderRadius: RADIUS.md, background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)' }}
      >
        {saving ? <CircularProgress size={20} color="inherit" /> : 'Record Payment'}
      </Button>
    </Box>
  );

  return (
    <DrawerShell open={open} onClose={handleClose} anchor={anchor} ariaLabel="Record payment" footer={footer}>
      <DrawerHeader title="Record Payment" onClose={handleClose} />
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Plan (optional)</InputLabel>
          <Select
            value={form.plan_id}
            onChange={(e) => handlePlanSelect(e.target.value)}
            label="Plan (optional)"
          >
            <MenuItem value=""><em>No plan / Custom amount</em></MenuItem>
            {plans.map((plan) => (
              <MenuItem key={plan.id} value={plan.id}>
                {plan.name} — ₹{plan.price_cents} ({plan.duration_days} days)
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Amount"
          type="number"
          value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })}
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
          }}
          helperText={form.plan_id ? 'Auto-filled from plan (editable)' : 'Enter amount in rupees'}
        />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Payment Method</InputLabel>
          <Select
            value={form.payment_method}
            onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
            label="Payment Method"
          >
            <MenuItem value="cash">Cash</MenuItem>
            <MenuItem value="upi">UPI</MenuItem>
            <MenuItem value="card">Card</MenuItem>
            <MenuItem value="net_banking">Net Banking</MenuItem>
            <MenuItem value="wallet">Wallet</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Transaction ID (optional)"
          value={form.transaction_id}
          onChange={(e) => setForm({ ...form, transaction_id: e.target.value })}
          fullWidth
          sx={{ mb: 2 }}
        />

        <TextField
          label="Notes (optional)"
          multiline
          rows={2}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          fullWidth
        />
      </Box>
    </DrawerShell>
  );
}

export default StudentPaymentDrawer;
